import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { CheckboxModule } from 'primeng/checkbox';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MasterPolicyService, MasterPolicyDto, MasterPolicy } from '../../services/master-policy';
import { PremiumRuleService, PremiumRuleDto, PremiumRule } from '../../services/premium-rule';
import {
  PremiumRuleDetailService,
  PremiumRuleDetailDto,
  PremiumRuleDetail,
} from '../../services/premium-rule-detail';
import { ClientService, Client } from '../../services/client';
import { PartnerService } from '../../services/partner';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';
import { IndDatePipe } from '../../pipes/ind-date';

/**
 * Product Master — the single page for the whole product hierarchy, mirroring the
 * client/partner master-detail pattern:
 *   products (master policies) → premium rules of the selected product → benefit
 *   breakup of the selected rule, all inline (no modal-in-modal).
 *
 * The Doctor Consultation price lives on the RULE here (not the benefit breakup):
 * it is entered as the ENTIRE amount (GST-inclusive) and the pre-GST fee is derived,
 * exactly like Gross Premium ↔ Base Premium.
 */
@Component({
  selector: 'app-product-master',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    SelectModule,
    DatePickerModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    CheckboxModule,
    ToggleSwitchModule,
    IndDatePipe,
  ],
  templateUrl: './product-master.html',
  styleUrls: ['./product-master.css'],
})
export class ProductMasterPage implements OnInit {
  private fb = inject(FormBuilder);
  private policySvc = inject(MasterPolicyService);
  private ruleSvc = inject(PremiumRuleService);
  private detailSvc = inject(PremiumRuleDetailService);
  private clientSvc = inject(ClientService);
  private partnerSvc = inject(PartnerService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  private pageTitleSvc = inject(PageTitleService);

  readonly currencies = ['INR', 'USD', 'EUR', 'GBP'];
  readonly statuses = ['ACTIVE', 'INACTIVE'];
  readonly ownerTypeOptions = [
    { label: 'Client', value: 'Client' },
    { label: 'Partner', value: 'Partner' },
  ];

  /** Fallback heading when no Menu Master row exists yet for /product-master. */
  readonly title = computed(() => this.pageTitleSvc.title() || 'Product Master');

  // ===================== products (master policies) =====================
  loadingPolicies = signal(false);
  policies = signal<MasterPolicy[]>([]);
  policySearch = signal('');
  selectedPolicy = signal<MasterPolicy | null>(null);

  filteredPolicies = computed(() => {
    const q = this.policySearch().trim().toLowerCase();
    if (!q) return this.policies();
    return this.policies().filter(
      (p) =>
        (p.masterPolicyName || '').toLowerCase().includes(q) ||
        (p.insuranceCompanyName || '').toLowerCase().includes(q) ||
        (p.masterPolicyNumber || '').toLowerCase().includes(q),
    );
  });

  savingPolicy = signal(false);
  editingPolicy = signal(false);
  showPolicyModal = signal(false);

  // Owner pickers for the product form (same behavior as the old Master Policies page).
  clients = signal<Client[]>([]);
  partners = signal<any[]>([]);
  ownerTypeSig = signal<string>('Client');

  ownerOptions = computed(() => {
    if (this.ownerTypeSig() === 'Partner') {
      return this.partners().map((p) => ({ label: this.pName(p), value: this.pId(p) }));
    }
    return this.clients().map((c) => ({ label: `${c.clientName} (${c.clientCode})`, value: c.id }));
  });

  private pId = (p: any): number => p?.Id ?? p?.id ?? p?.PartnerId ?? p?.partnerId;
  private pName = (p: any): string =>
    p?.PartnerName ?? p?.partnerName ?? p?.Name ?? p?.name ?? `Partner #${this.pId(p)}`;

  ownerLabel(p: MasterPolicy): string {
    if (!p.ownerType || p.ownerId == null) return '—';
    if (p.ownerType === 'Partner') {
      const match = this.partners().find((x) => this.pId(x) === p.ownerId);
      return `Partner: ${match ? this.pName(match) : '#' + p.ownerId}`;
    }
    const c = this.clients().find((x) => x.id === p.ownerId);
    return `Client: ${c ? c.clientName : '#' + p.ownerId}`;
  }

  policyForm: FormGroup = this.fb.group(
    {
      id: [0],
      masterPolicyName: ['', Validators.required],
      insuranceCompanyName: ['', Validators.required],
      masterPolicyNumber: ['', Validators.required],
      policyStartDate: [null, Validators.required],
      years: [1, [Validators.required, Validators.min(1)]],
      // Derived from start date + term, never edited directly.
      policyEndDate: [{ value: null, disabled: true }],
      currency: ['INR', Validators.required],
      seriesSuffix: [''],
      seriesStartNo: [1, [Validators.required, Validators.min(0)]],
      seriesEndNo: [1000, [Validators.required, Validators.min(0)]],
      status: ['ACTIVE', Validators.required],
      docEnabled: [false],
      ownerType: ['Client', Validators.required],
      ownerId: [null as number | null, Validators.required],
    },
    { validators: [this.dateRange, this.seriesRange] },
  );

  private dateRange(g: AbstractControl): ValidationErrors | null {
    const s = g.get('policyStartDate')?.value;
    const e = g.get('policyEndDate')?.value;
    return s && e && e < s ? { dateRange: true } : null;
  }

  private seriesRange(g: AbstractControl): ValidationErrors | null {
    const s = Number(g.get('seriesStartNo')?.value);
    const e = Number(g.get('seriesEndNo')?.value);
    return Number.isFinite(s) && Number.isFinite(e) && e < s ? { seriesRange: true } : null;
  }

  // ===================== rules of the selected product =====================
  loadingRules = signal(false);
  rules = signal<PremiumRule[]>([]);
  selectedRule = signal<PremiumRule | null>(null);

  savingRule = signal(false);
  editingRule = signal(false);
  showRuleModal = signal(false);

  ruleForm: FormGroup = this.fb.group(
    {
      id: [0],
      masterPolicyId: [null, Validators.required],
      coverageType: [null, Validators.required],
      premiumCode: ['', Validators.required],
      ageFrom: [0, [Validators.required, Validators.min(0)]],
      ageTo: [0, [Validators.required, Validators.min(0)]],
      basePremium: [0, [Validators.required, Validators.min(0)]],
      gstPercentage: [18, [Validators.required, Validators.min(0), Validators.max(100)]],
      policyTermYears: [1, [Validators.required, Validators.min(1)]],
      grossPremium: [0, [Validators.min(0)]], // ENTIRE plan amount — base is derived
      doctorGross: [0, [Validators.min(0)]], // ENTIRE doctor-consultation amount — fee derived
      doctorConsultant: [0], // derived pre-GST fee actually sent to the API
      effectiveDate: [null],
      endDate: [null],
      neverExpires: [true],
      status: ['ACTIVE', Validators.required],
    },
    { validators: [this.ageRange] },
  );

  // Live breakup shown in the rule dialog (mirrors the server's whole-rupee rounding).
  planBasePreview = signal(0);
  doctorBasePreview = signal(0);
  gstPreview = signal(0);
  totalPreview = signal(0);

  private ageRange(g: AbstractControl): ValidationErrors | null {
    const f = Number(g.get('ageFrom')?.value);
    const t = Number(g.get('ageTo')?.value);
    return Number.isFinite(f) && Number.isFinite(t) && f > t ? { ageRange: true } : null;
  }

  // ===================== benefit breakup of the selected rule =====================
  detailsLoading = signal(false);
  details = signal<PremiumRuleDetail[]>([]);
  showDetailForm = signal(false);
  editingDetail = signal(false);
  savingDetail = signal(false);

  // Doctor Consultation is NOT here any more — it lives on the rule (main price breakup).
  detailForm: FormGroup = this.fb.group({
    id: [0],
    premiumRulesID: [0, Validators.required],
    masterPolicyId: [0, Validators.required],
    criticalIllness: [null],
    dailyHospitalisation: [null],
    icUhospitalization: [null],
    accidentalDeath: [null],
    permanentTotalDisablement: [null],
    permanentPartialDisablement: [null],
    convalescenceCover: [null],
    otherType: [''],
    other: [null],
    isActive: [true],
  });

  // ===================== lifecycle =====================
  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.loadPolicies();
      this.loadOwners();
    }
    this.policyForm.get('policyStartDate')?.valueChanges.subscribe(() => this.syncEndDate());
    this.policyForm.get('years')?.valueChanges.subscribe(() => this.syncEndDate());
    this.policyForm
      .get('ownerType')
      ?.valueChanges.subscribe((t: string) => this.ownerTypeSig.set(t || 'Client'));
  }

  private loadOwners() {
    this.clientSvc.cltGetAll().subscribe({
      next: (res: any) => this.clients.set(Array.isArray(res?.data) ? res.data : []),
    });
    this.partnerSvc.getAll().subscribe({
      next: (res: any) => {
        const data = res?.data ?? res ?? [];
        this.partners.set(Array.isArray(data) ? data : []);
      },
    });
  }

  // ===================== shared helpers =====================
  private r2(n: number): number {
    return Math.round((Number(n) || 0) * 100) / 100;
  }

  /** Whole-rupee rounding, same as the server (MidpointRounding.AwayFromZero). */
  private r0(n: number): number {
    return Math.sign(n) * Math.round(Math.abs(Number(n) || 0));
  }

  private parseDate(v: any): Date | null {
    if (!v) return null;
    const d = new Date(String(v).slice(0, 10) + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
  }

  private toIso(v: any): string {
    if (!v) return '';
    const d = v instanceof Date ? v : new Date(v);
    if (isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  statusSeverity(s: string): 'success' | 'secondary' {
    return s === 'ACTIVE' ? 'success' : 'secondary';
  }

  // ===================== products: list + select =====================
  loadPolicies(keepSelection = false) {
    this.loadingPolicies.set(true);
    this.policySvc.getAll().subscribe({
      next: (res: any) => {
        this.loadingPolicies.set(false);
        const data = res?.data ?? res ?? [];
        const list: MasterPolicy[] = Array.isArray(data) ? data : [];
        this.policies.set(list);
        if (keepSelection && this.selectedPolicy()) {
          const again = list.find((p) => p.id === this.selectedPolicy()!.id) || null;
          this.selectedPolicy.set(again);
          if (!again) this.clearRuleState();
        }
      },
      error: () => this.loadingPolicies.set(false),
    });
  }

  selectPolicy(p: MasterPolicy) {
    if (this.selectedPolicy()?.id === p.id) return;
    this.selectedPolicy.set(p);
    this.clearRuleState();
    this.loadRules();
  }

  private clearRuleState() {
    this.rules.set([]);
    this.selectedRule.set(null);
    this.details.set([]);
  }

  // ===================== products: add / edit / delete =====================
  openAddPolicy() {
    this.editingPolicy.set(false);
    this.policyForm.reset({
      id: 0,
      masterPolicyName: '',
      insuranceCompanyName: '',
      masterPolicyNumber: '',
      policyStartDate: null,
      years: 1,
      policyEndDate: null,
      currency: 'INR',
      seriesSuffix: '',
      seriesStartNo: 1,
      seriesEndNo: 1000,
      status: 'ACTIVE',
      docEnabled: false,
      ownerType: 'Client',
      ownerId: null,
    });
    this.ownerTypeSig.set('Client');
    this.policyForm.get('masterPolicyNumber')?.enable();
    this.showPolicyModal.set(true);
  }

  openEditPolicy(p: MasterPolicy) {
    this.editingPolicy.set(true);
    const start = this.parseDate(p.policyStartDate);
    const end = this.parseDate(p.policyEndDate);
    this.policyForm.reset({
      id: p.id,
      masterPolicyName: p.masterPolicyName ?? '',
      insuranceCompanyName: p.insuranceCompanyName ?? '',
      masterPolicyNumber: p.masterPolicyNumber ?? '',
      policyStartDate: start,
      years: this.yearsFromDates(start, end),
      policyEndDate: end,
      currency: p.currency ?? 'INR',
      seriesSuffix: p.seriesSuffix ?? '',
      seriesStartNo: p.seriesStartNo ?? 0,
      seriesEndNo: p.seriesEndNo ?? 0,
      status: p.status ?? 'ACTIVE',
      docEnabled: p.docEnabled === true,
      ownerType: p.ownerType ?? 'Client',
      ownerId: p.ownerId ?? null,
    });
    this.ownerTypeSig.set(p.ownerType ?? 'Client');
    // MasterPolicyNumber is fixed once created.
    this.policyForm.get('masterPolicyNumber')?.disable();
    this.showPolicyModal.set(true);
  }

  onOwnerTypeChange() {
    this.policyForm.get('ownerId')?.setValue(null);
  }

  private syncEndDate() {
    const start = this.policyForm.get('policyStartDate')?.value as Date | null;
    const years = Number(this.policyForm.get('years')?.value);
    this.policyForm.get('policyEndDate')?.setValue(this.computeEndDate(start, years), { emitEvent: false });
  }

  private computeEndDate(start: Date | null, years: number): Date | null {
    if (!start || !Number.isFinite(years) || years < 1) return null;
    const d = new Date(start);
    if (isNaN(d.getTime())) return null;
    d.setFullYear(d.getFullYear() + years);
    d.setDate(d.getDate() - 1);
    return d;
  }

  private yearsFromDates(start: Date | null, end: Date | null): number {
    if (!start || !end) return 1;
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return 1;
    e.setDate(e.getDate() + 1);
    let y = e.getFullYear() - s.getFullYear();
    if (e.getMonth() < s.getMonth() || (e.getMonth() === s.getMonth() && e.getDate() < s.getDate())) y--;
    return Math.max(1, y);
  }

  closePolicyModal() {
    this.showPolicyModal.set(false);
  }

  savePolicy() {
    if (this.policyForm.invalid) {
      this.policyForm.markAllAsTouched();
      if (this.policyForm.errors?.['dateRange']) this.toast.warning('End date must be on or after start date');
      else if (this.policyForm.errors?.['seriesRange']) this.toast.warning('Series end must be ≥ series start');
      else this.toast.warning('Please fill in all required fields');
      return;
    }

    const v = this.policyForm.getRawValue();
    const dto: MasterPolicyDto = {
      masterPolicyName: v.masterPolicyName,
      insuranceCompanyName: v.insuranceCompanyName,
      masterPolicyNumber: v.masterPolicyNumber,
      policyStartDate: this.toIso(v.policyStartDate),
      policyEndDate: this.toIso(v.policyEndDate),
      currency: v.currency,
      seriesSuffix: (v.seriesSuffix || '').trim() || null,
      seriesStartNo: Number(v.seriesStartNo),
      seriesEndNo: Number(v.seriesEndNo),
      status: v.status,
      ownerType: v.ownerType,
      ownerId: v.ownerId != null ? Number(v.ownerId) : null,
      docEnabled: !!v.docEnabled,
    };
    if (this.editingPolicy()) dto.id = v.id;

    this.savingPolicy.set(true);
    const req = this.editingPolicy() ? this.policySvc.update(dto) : this.policySvc.insert(dto);
    req.subscribe({
      next: (res: any) => {
        this.savingPolicy.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save product');
          return;
        }
        this.toast.success(this.editingPolicy() ? 'Product updated' : 'Product created');
        this.closePolicyModal();
        this.loadPolicies(true);
      },
      error: () => this.savingPolicy.set(false),
    });
  }

  async removePolicy(p: MasterPolicy) {
    const ok = await this.toast.confirm(
      `"${p.masterPolicyName}" and its rules will no longer be available.`,
      'Delete product?',
      'Delete',
    );
    if (!ok) return;
    this.policySvc.delete(p.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete');
          return;
        }
        this.toast.success('Product deleted');
        if (this.selectedPolicy()?.id === p.id) {
          this.selectedPolicy.set(null);
          this.clearRuleState();
        }
        this.loadPolicies(true);
      },
    });
  }

  // ===================== rules: list + select =====================
  loadRules(keepSelection = false) {
    const p = this.selectedPolicy();
    if (!p) return;
    this.loadingRules.set(true);
    this.ruleSvc.getByMasterPolicy(p.id).subscribe({
      next: (res: any) => {
        this.loadingRules.set(false);
        const data = res?.data ?? res ?? [];
        const list: PremiumRule[] = Array.isArray(data) ? data : [];
        this.rules.set(list);
        if (keepSelection && this.selectedRule()) {
          const again = list.find((r) => r.id === this.selectedRule()!.id) || null;
          this.selectedRule.set(again);
          if (!again) this.details.set([]);
        }
      },
      error: () => this.loadingRules.set(false),
    });
  }

  selectRule(r: PremiumRule) {
    if (this.selectedRule()?.id === r.id) return;
    this.selectedRule.set(r);
    this.loadDetails();
  }

  /** Gross of the plan alone (no doctor), whole rupees — display only. */
  grossOf(r: PremiumRule): number {
    const base = Number(r.basePremium) || 0;
    const gst = Number(r.gstPercentage) || 0;
    return this.r0(base * (1 + gst / 100));
  }

  /** GST-inclusive doctor-consultation amount — display only ("entire amount"). */
  doctorGrossOf(r: PremiumRule): number {
    const doc = Number(r.doctorConsultant) || 0;
    const gst = Number(r.gstPercentage) || 0;
    const base = Number(r.basePremium) || 0;
    // Same maths the quote uses: the GST-inclusive DIFFERENCE the member pays when opting in.
    return this.r0((base + doc) * (1 + gst / 100)) - this.r0(base * (1 + gst / 100));
  }

  // ===================== rules: add / edit / delete =====================
  private num(name: string): number {
    return Number(this.ruleForm.get(name)?.value) || 0;
  }

  /** Base fields are always derived from the ENTIRE amounts + GST%. */
  private recalcFromGross() {
    const gst = this.num('gstPercentage');
    const gross = this.num('grossPremium');
    const doctorGross = this.num('doctorGross');
    const base = this.r2(gross / (1 + gst / 100));
    const doctorBase = this.r2(doctorGross / (1 + gst / 100));
    this.ruleForm.get('basePremium')?.setValue(base, { emitEvent: false });
    this.ruleForm.get('doctorConsultant')?.setValue(doctorBase, { emitEvent: false });
    this.refreshPreview();
  }

  onRuleAmountChange() {
    this.recalcFromGross();
  }

  /**
   * Preview mirrors the server (MemberRepository): rawBase = base + doctor(base);
   * gross rounded to whole rupees; GST derived FROM the gross; base = remainder.
   */
  private refreshPreview() {
    const gst = this.num('gstPercentage');
    const base = this.num('basePremium');
    const doctorBase = this.num('doctorConsultant');
    const rawBase = base + doctorBase;
    const total = this.r0((rawBase * (100 + gst)) / 100);
    const gstAmt = gst > 0 ? this.r0((total * gst) / (100 + gst)) : 0;
    this.planBasePreview.set(this.r2(base));
    this.doctorBasePreview.set(this.r2(doctorBase));
    this.gstPreview.set(gstAmt);
    this.totalPreview.set(total);
  }

  openAddRule() {
    const p = this.selectedPolicy();
    if (!p) return;
    this.editingRule.set(false);
    this.ruleForm.reset({
      id: 0,
      masterPolicyId: p.id,
      // Coverage Type auto-fills with the product (master policy) name; still editable.
      coverageType: p.masterPolicyName || '',
      premiumCode: '',
      ageFrom: 0,
      ageTo: 0,
      basePremium: 0,
      gstPercentage: 18,
      policyTermYears: 1,
      grossPremium: 0,
      doctorGross: 0,
      doctorConsultant: 0,
      effectiveDate: null,
      endDate: null,
      neverExpires: true,
      status: 'ACTIVE',
    });
    this.ruleForm.get('endDate')?.disable();
    this.recalcFromGross();
    this.showRuleModal.set(true);
  }

  openEditRule(r: PremiumRule) {
    this.editingRule.set(true);
    const gst = Number(r.gstPercentage) || 0;
    this.ruleForm.reset({
      id: r.id,
      masterPolicyId: r.masterPolicyId,
      coverageType: r.coverageType || this.selectedPolicy()?.masterPolicyName || '',
      premiumCode: r.premiumCode ?? '',
      ageFrom: r.ageFrom ?? 0,
      ageTo: r.ageTo ?? 0,
      basePremium: r.basePremium ?? 0,
      gstPercentage: gst || 18,
      policyTermYears: r.policyTermYears ?? 1,
      // ENTIRE amounts are reconstructed from the stored pre-GST values.
      grossPremium: this.r2((Number(r.basePremium) || 0) * (1 + gst / 100)),
      doctorGross: this.r2((Number(r.doctorConsultant) || 0) * (1 + gst / 100)),
      doctorConsultant: r.doctorConsultant ?? 0,
      effectiveDate: this.parseDate(r.effectiveDate),
      endDate: this.parseDate(r.endDate),
      neverExpires: r.endDate == null,
      status: r.status ?? 'ACTIVE',
    });
    const end = this.ruleForm.get('endDate');
    if (r.endDate == null) end?.disable();
    else end?.enable();
    this.recalcFromGross();
    this.showRuleModal.set(true);
  }

  toggleNeverExpires(checked: boolean) {
    this.ruleForm.get('neverExpires')?.setValue(checked);
    const end = this.ruleForm.get('endDate');
    if (checked) {
      end?.reset(null);
      end?.disable();
    } else {
      end?.enable();
    }
  }

  closeRuleModal() {
    this.showRuleModal.set(false);
  }

  saveRule() {
    if (this.ruleForm.invalid) {
      this.ruleForm.markAllAsTouched();
      if (this.ruleForm.errors?.['ageRange']) this.toast.warning('Age From must be ≤ Age To');
      else this.toast.warning('Please fill in all required fields');
      return;
    }

    const v = this.ruleForm.getRawValue();
    const dto: PremiumRuleDto = {
      masterPolicyId: Number(v.masterPolicyId),
      coverageType: v.coverageType,
      premiumCode: v.premiumCode,
      ageFrom: Number(v.ageFrom),
      ageTo: Number(v.ageTo),
      basePremium: Number(v.basePremium),
      gstPercentage: Number(v.gstPercentage),
      doctorConsultant: Number(v.doctorConsultant) || 0,
      policyTermYears: Number(v.policyTermYears),
      effectiveDate: this.toIso(v.effectiveDate) || null,
      endDate: v.neverExpires ? null : this.toIso(v.endDate) || null,
      status: v.status,
    };
    if (this.editingRule()) dto.id = v.id;

    this.savingRule.set(true);
    const req = this.editingRule() ? this.ruleSvc.update(dto) : this.ruleSvc.insert(dto);
    req.subscribe({
      next: (res: any) => {
        this.savingRule.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save rule');
          return;
        }
        this.toast.success(this.editingRule() ? 'Premium rule updated' : 'Premium rule created');
        this.closeRuleModal();
        this.loadRules(true);
        // The save also mirrors the doctor fee into the breakup row — refresh it if visible.
        if (this.selectedRule()) this.loadDetails();
      },
      error: () => this.savingRule.set(false),
    });
  }

  async removeRule(r: PremiumRule) {
    const ok = await this.toast.confirm(
      `"${r.premiumCode}" will be deleted.`,
      'Delete premium rule?',
      'Delete',
    );
    if (!ok) return;
    this.ruleSvc.delete(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete');
          return;
        }
        this.toast.success('Premium rule deleted');
        if (this.selectedRule()?.id === r.id) {
          this.selectedRule.set(null);
          this.details.set([]);
        }
        this.loadRules(true);
      },
    });
  }

  // ===================== benefit breakup (inline, per selected rule) =====================
  loadDetails() {
    const r = this.selectedRule();
    if (!r) return;
    this.detailsLoading.set(true);
    this.detailSvc.getByPremiumRule(r.id).subscribe({
      next: (res: any) => {
        this.detailsLoading.set(false);
        const data = res?.data ?? res ?? [];
        this.details.set(Array.isArray(data) ? data : []);
      },
      error: () => this.detailsLoading.set(false),
    });
  }

  openAddDetail() {
    const rule = this.selectedRule();
    if (!rule) return;
    this.editingDetail.set(false);
    this.detailForm.reset({
      id: 0,
      premiumRulesID: rule.id,
      masterPolicyId: rule.masterPolicyId,
      criticalIllness: null,
      dailyHospitalisation: null,
      icUhospitalization: null,
      accidentalDeath: null,
      permanentTotalDisablement: null,
      permanentPartialDisablement: null,
      convalescenceCover: null,
      otherType: '',
      other: null,
      isActive: true,
    });
    this.showDetailForm.set(true);
  }

  openEditDetail(d: PremiumRuleDetail) {
    this.editingDetail.set(true);
    this.detailForm.reset({
      id: d.id,
      premiumRulesID: d.premiumRulesID,
      masterPolicyId: d.masterPolicyId,
      criticalIllness: d.criticalIllness ?? null,
      dailyHospitalisation: d.dailyHospitalisation ?? null,
      icUhospitalization: d.icUhospitalization ?? null,
      accidentalDeath: d.accidentalDeath ?? null,
      permanentTotalDisablement: d.permanentTotalDisablement ?? null,
      permanentPartialDisablement: d.permanentPartialDisablement ?? null,
      convalescenceCover: d.convalescenceCover ?? null,
      otherType: d.otherType ?? '',
      other: d.other ?? null,
      isActive: d.isActive ?? true,
    });
    this.showDetailForm.set(true);
  }

  closeDetailForm() {
    this.showDetailForm.set(false);
  }

  private nz(v: any): number | null {
    return v === '' || v == null || isNaN(Number(v)) ? null : Number(v);
  }

  saveDetail() {
    if (this.detailForm.invalid) {
      this.detailForm.markAllAsTouched();
      this.toast.warning('Please complete the required fields');
      return;
    }

    const v = this.detailForm.getRawValue();
    const dto: PremiumRuleDetailDto = {
      premiumRulesID: Number(v.premiumRulesID),
      masterPolicyId: Number(v.masterPolicyId),
      criticalIllness: this.nz(v.criticalIllness),
      dailyHospitalisation: this.nz(v.dailyHospitalisation),
      icUhospitalization: this.nz(v.icUhospitalization),
      accidentalDeath: this.nz(v.accidentalDeath),
      permanentTotalDisablement: this.nz(v.permanentTotalDisablement),
      permanentPartialDisablement: this.nz(v.permanentPartialDisablement),
      convalescenceCover: this.nz(v.convalescenceCover),
      // The server keeps DoctorConsultant mirrored from the rule; whatever is sent is ignored.
      doctorConsultant: null,
      otherType: (v.otherType || '').toString().trim() || null,
      other: this.nz(v.other),
      isActive: !!v.isActive,
    };
    if (this.editingDetail()) dto.id = v.id;

    this.savingDetail.set(true);
    const req = this.editingDetail() ? this.detailSvc.update(dto) : this.detailSvc.insert(dto);
    req.subscribe({
      next: (res: any) => {
        this.savingDetail.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save detail');
          return;
        }
        this.toast.success(this.editingDetail() ? 'Benefit breakup updated' : 'Benefit breakup added');
        this.closeDetailForm();
        this.loadDetails();
      },
      error: () => this.savingDetail.set(false),
    });
  }

  async removeDetail(d: PremiumRuleDetail) {
    const ok = await this.toast.confirm(
      'This benefit breakup row will be deleted.',
      'Delete benefit breakup?',
      'Delete',
    );
    if (!ok) return;
    this.detailSvc.delete(d.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete');
          return;
        }
        this.toast.success('Benefit breakup deleted');
        this.loadDetails();
      },
    });
  }
}
